import { t } from '../utils/translations';

interface LoginRequest {
  username: string;
  password: string;
}

interface LoginResponse {
  token: string;
  refresh_token: string;
  refresh_token_expiration: number;
}

export const loginUser = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/login_check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 401) {
        throw new Error(translatedMessage || t('auth.invalidCredentials'));
      } else if (response.status === 400) {
        throw new Error(translatedMessage || t('auth.invalidLoginData'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(translatedMessage || t('auth.loginFailed'));
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};

export const refreshToken = async (refresh_token: string): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/token/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(t('auth.refreshTokenExpired'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(t('auth.refreshFailed'));
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};

interface RegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
  recaptchaToken: string;
}

interface RegisterResponse {
  message: string;
  id: string;
}

export const registerUser = async (data: RegisterRequest): Promise<RegisterResponse> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/user-register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('auth.validationError'));
      } else if (response.status === 400) {
        throw new Error(translatedMessage || t('auth.invalidRegistrationData'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(translatedMessage || t('auth.registrationFailed'));
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};

interface ActivateUserResponse {
  message: string;
}

export const activateUser = async (userId: string): Promise<ActivateUserResponse> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/user-activate`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('auth.validationError'));
      } else if (response.status === 400) {
        throw new Error(translatedMessage || t('auth.activationFailed'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(translatedMessage || t('auth.activationFailed'));
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};

interface ForgotPasswordRequest {
  email: string;
  recaptchaToken: string;
}

export const forgotPassword = async (data: ForgotPasswordRequest): Promise<void> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/user/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('auth.validationError'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(translatedMessage || t('auth.forgotPasswordFailed'));
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};

interface ResetPasswordRequest {
  password: string;
  passwordConfirmation: string;
  recaptchaToken: string;
}

export const resetPassword = async (requestId: string, data: ResetPasswordRequest): Promise<void> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/user/reset-password/${requestId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 400) {
        throw new Error(translatedMessage || t('auth.invalidOrExpiredResetLink'));
      } else if (response.status === 422) {
        throw new Error(translatedMessage || t('auth.validationError'));
      } else if (response.status >= 500) {
        throw new Error(t('auth.serverError'));
      } else {
        throw new Error(translatedMessage || t('auth.resetPasswordFailed'));
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('auth.connectionError'));
    }
    throw error;
  }
};